import { ReactComponent as AddIcon } from "Icons/vuesax/add.svg";
import { ReactComponent as CalendarIcon } from "Icons/vuesax/calendar.svg";
import { ReactComponent as CloseCircleIcon } from "Icons/vuesax/close-circle.svg";
import { ReactComponent as CloudUploadOutlined } from "Icons/vuesax/cloud-add.svg";
import { ReactComponent as DocumentUploadIcon } from "Icons/vuesax/document-upload.svg";
import { ReactComponent as InfoCircleIcon } from "Icons/vuesax/info-circle.svg";
import { ReactComponent as TickCircleIcon } from "Icons/vuesax/tick-circle.svg";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  Upload
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload/interface";
import { uploadFile } from "api/services/UploadJobsService";
import { getVoiceFileExtensions } from "api/services/VoiceFileExtensionsService";
import { TUploadJobFile, TVoiceFileExtensions, UploadStatus } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import ErrorViewer from "./ErrorViewer";
import { useLayoutContext } from "./MainContainer";

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; //1 GB

const uuidv4 = () =>
  "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
  );

type TUploadModalProps = {
  onClose: () => void;
  open: boolean;
};

const UploadStats = ({ fileList }: { fileList: TUploadJobFile[] }) => {
  const ready = fileList.filter(f => f.uploadStatus === UploadStatus.Ready).length;
  const uploaded = fileList.filter(f => f.uploadStatus === UploadStatus.Uploaded).length;
  const skipped = fileList.filter(f => f.uploadStatus === UploadStatus.Skipped).length;
  const failed = fileList.filter(f => f.uploadStatus === UploadStatus.Failed).length;
  return (
    <Flex align="center" justify="space-evenly">
      <Typography.Text type="secondary">{`Ready: ${ready}`}</Typography.Text>
      <Typography.Text type="success">{`Uploaded: ${uploaded}`}</Typography.Text>
      <Typography.Text type="danger">{`Failed: ${failed}`}</Typography.Text>
      <Typography.Text type="warning">{`Skipped: ${skipped}`}</Typography.Text>
    </Flex>
  );
};

export default function UploadModal({ onClose, open }: Readonly<TUploadModalProps>) {
  const [fileList, setFileList] = useState<TUploadJobFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [fileExtensions, setFileExtensions] = useState<TVoiceFileExtensions[]>([]);
  const { account, locations, providers } = usePicklists();
  const { accountId } = useTDAuthentication();
  const { toggleDrawer, isSmallScreen } = useLayoutContext();

  useEffect(() => {
    setLoading(true);
    getVoiceFileExtensions()
      .then(setFileExtensions)
      .catch(setError)
      .finally(() => setLoading(false));
    setSelectedLocationId(account?.primaryLocationId ?? locations?.at(0)?.locationId ?? null);
    setSelectedProviderId(providers?.at(0)?.providerId ?? null);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const columns: ColumnsType<TUploadJobFile> = [
    {
      title: "File name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: "30%",
      render: (name: string) => (
        <Tooltip title={name}>
          <Typography.Text ellipsis>{name}</Typography.Text>
        </Tooltip>
      )
    },
    {
      title: "Date of service",
      dataIndex: "dateOfService",
      key: "dateOfService",
      width: "35%",
      render: (dateOfService, record) => (
        <DatePicker
          allowClear={false}
          size="small"
          value={dayjs(dateOfService, "MM/DD/YYYY")}
          format="MM/DD/YYYY"
          onChange={val => updateState(record, { dateOfService: val.format("MM/DD/YYYY") })}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: "Status",
      dataIndex: "uploadStatus",
      key: "uploadStatus",
      width: "35%",
      render: (status: UploadStatus) => {
        const colorMap = {
          [UploadStatus.Ready]: "#12121280",
          [UploadStatus.Success]: "#00BE1E",
          [UploadStatus.Failed]: "#FF3535",
          [UploadStatus.Skipped]: "#FF7A00",
          [UploadStatus.Uploading]: "#12121280",
          [UploadStatus.Uploaded]: "#00BE1E"
        };

        const color = colorMap[status];

        return (
          <span style={{ color, display: "flex", alignItems: "center" }}>
            {statusIconMap[status]} <span style={{ marginLeft: 8 }}>{status}</span>
          </span>
        );
      }
    }
  ];

  const updateState = (job: TUploadJobFile, values: Partial<TUploadJobFile>) => {
    const updatedJob = { ...job, ...values };
    setFileList(files => files.map(f => (f.uid == job.uid ? updatedJob : f)));
    return updatedJob;
  };

  const statusIconMap = {
    [UploadStatus.Ready]: <TickCircleIcon style={{ color: "#12121280" }} />,
    [UploadStatus.Success]: <TickCircleIcon style={{ color: "#00BE1E" }} />,
    [UploadStatus.Failed]: <InfoCircleIcon style={{ color: "#FF3535" }} />,
    [UploadStatus.Skipped]: <CloseCircleIcon style={{ color: "#FF7A00" }} />,
    [UploadStatus.Uploading]: <DocumentUploadIcon style={{ color: "#12121280" }} />,
    [UploadStatus.Uploaded]: <TickCircleIcon style={{ color: "#00BE1E" }} />
  };

  const uploadJob = async (job: TUploadJobFile): Promise<TUploadJobFile> => {
    return Promise.resolve(job)
      .then(j => updateState(j, { uploadStatus: UploadStatus.Uploading }))
      .then(j => uploadFile(j))
      .then(j => updateState(j, { uploadStatus: UploadStatus.Uploaded }))
      .catch(e => {
        setError(e);
        return updateState(job, { uploadStatus: UploadStatus.Failed });
      });
  };

  const handleUpload = async () => {
    setUploading(true);
    setError(null);
    const uploads = fileList
      .filter(
        f => f.uploadStatus === UploadStatus.Ready || f.uploadStatus === UploadStatus.Failed
      )
      .map(f =>
        uploadJob({
          ...f,
          accountId,
          providerId: selectedProviderId,
          locationId: selectedLocationId
        })
      );
    Promise.all(uploads).finally(() => setUploading(false));
  };

  const validateFile = (file: File) => {
    if (fileList.some(f => f.name === file.name)) {
      return { status: UploadStatus.Skipped };
    }

    if (file.size && file.size > MAX_FILE_SIZE_BYTES) {
      return { status: UploadStatus.Skipped };
    }

    const fileExt = file.name.split(".").pop()?.toLowerCase();
    if (!fileExtensions.some(e => e.fileExtension.toLowerCase() === fileExt)) {
      return { status: UploadStatus.Skipped };
    }
    return {
      status: UploadStatus.Ready,
      isVoiceFile: fileExtensions.some(
        e => e.fileExtension.toLowerCase() === fileExt && e.isVoiceFile
      )
    };
  };

  const addNewFile = (file: File) => {
    const validStatus = validateFile(file);
    setFileList(prev => [
      ...prev,
      {
        uid: uuidv4(),
        name: file.name,
        isVoiceFile: validStatus.isVoiceFile ?? false,
        dateOfService: dayjs().format("MM/DD/YYYY"),
        uploadStatus: validStatus.status,
        file
      }
    ]);
    return false;
  };

  const uploadProps = {
    multiple: true,
    beforeUpload: addNewFile,
    fileList,
    onRemove: ({ uid }: UploadFile) =>
      setFileList(prev => prev.filter(item => item.uid !== uid)),
    showUploadList: false
  };

  const getFooter = () => {
    if (fileList.length === 0) return <></>;
    const buttonText = fileList.some(f => f.uploadStatus === UploadStatus.Failed)
      ? "Retry"
      : "Upload";
    const ready = fileList.some(
      f => f.uploadStatus === UploadStatus.Ready || f.uploadStatus === UploadStatus.Failed
    );

    return [
      <Button key="reset" onClick={reset} disabled={uploading}>
        Reset
      </Button>,
      <Button
        key="upload"
        type="primary"
        onClick={handleUpload}
        loading={uploading}
        disabled={!location || !selectedProviderId || uploading || !ready}>
        {uploading ? "Uploading..." : buttonText}
      </Button>
    ];
  };

  const reset = () => {
    setFileList([]);
    setError(null);
    setSelectedLocationId(account?.primaryLocationId ?? locations?.at(0)?.locationId ?? null);
    setSelectedProviderId(providers?.at(0)?.providerId ?? null);
  };
  interface FileCardProps {
    item: TUploadJobFile;
    cardStyle?: React.CSSProperties;
  }
  const FileCard: React.FC<FileCardProps> = ({ item, cardStyle = {} }) => {
    return (
      <Card key={item?.uid} style={{ width: "100%", backgroundColor: "#FFF", ...cardStyle }}>
        <Flex gap="middle" align="end" justify="space-between" style={{ marginBottom: "16px" }}>
          <Typography.Text style={{ fontSize: "1rem" }} className="font-1212" ellipsis={true}>
            {item.name}
          </Typography.Text>
          {item.uploadStatus}
        </Flex>
        <label htmlFor="date-of-service" className="input-label">
          Date of service
        </label>
        <DatePicker
          id="date-of-service"
          className="custom-datepicker"
          allowClear={false}
          size="small"
          value={dayjs(item?.dateOfService, "MM/DD/YYYY")}
          format="MM/DD/YYYY"
          onChange={val => updateState(item, { dateOfService: val.format("MM/DD/YYYY") })}
          suffixIcon={<CalendarIcon className="calendar-icon" />}
          style={{ height: "32px" }}
          popupStyle={{ zIndex: 9999 }}
        />
      </Card>
    );
  };

  return (
    <>
      {!isSmallScreen ? (
        <Modal
          centered={true}
          title={"Upload Job"}
          open={open}
          onCancel={() => {
            if (!uploading) {
              onClose();
            }
          }}
          style={{ minWidth: "33.25rem", minHeight: "23.75rem" }}
          footer={getFooter()}
          maskClosable={false}
          destroyOnClose={true}>
          {loading && (
            <Flex
              justify="space-around"
              style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
              <Spin tip="Loading, please wait..." />
            </Flex>
          )}
          {error && <ErrorViewer error={error} />}
          <Upload.Dragger
            {...uploadProps}
            style={{ padding: "4.31rem 7.31rem 4.31rem 7.31rem" }}>
            <Space direction="vertical" align="center">
              <CloudUploadOutlined className="custom-upload-icon" />
              <Typography.Text className="font-1212" style={{ fontSize: "1rem" }}>
                Choose a file or drag and drop here
              </Typography.Text>
              <Typography.Text className="font-7777" style={{ fontSize: ".75rem" }}>
                Maximum file size : 1 GB
              </Typography.Text>
              <Button type="primary">
                Browse File
              </Button>
            </Space>
          </Upload.Dragger>

          {fileList.length > 0 && (
            <Flex gap={8} vertical>
              <Row gutter={16}>
                <Col span={12}>
                  <Flex vertical align="center">
                    <Typography.Text>Location</Typography.Text>
                    <Select
                      key="location"
                      placeholder="Select"
                      variant="borderless"
                      className="drop-down-menu"
                      onChange={val => setSelectedLocationId(val)}
                      style={{ width: "100%" }}
                      options={locations}
                      fieldNames={{ label: "locationName", value: "locationId" }}
                      value={selectedLocationId}
                    />
                  </Flex>
                </Col>
                <Col span={12}>
                  <Flex vertical align="center">
                    <Typography.Text>Provider</Typography.Text>
                    <Select
                      placeholder="Select"
                      variant="borderless"
                      className="drop-down-menu"
                      onChange={val => setSelectedProviderId(val)}
                      key={account?.accountId}
                      style={{ width: "100%" }}
                      fieldNames={{ label: "providerName", value: "providerId" }}
                      options={providers}
                      value={selectedProviderId}
                    />
                  </Flex>
                </Col>
              </Row>
              <Card className="card-style" styles={{ body: { padding: "0" } }}>
                <Table
                  className="table-style"
                  columns={columns}
                  style={{ border: "none" }}
                  dataSource={fileList}
                  pagination={false}
                  rowKey="uid"
                  size="small"
                  scroll={{ y: "30rem" }}
                  components={{
                    body: {
                      cell: (props: any) => (
                        <td
                          {...props}
                          style={{
                            borderBottomWidth: "1px",
                            borderBottomColor: "#f0f0f0",
                            borderBottomStyle: "solid"
                          }}
                        />
                      )
                    }
                  }}
                />
              </Card>

              <UploadStats fileList={fileList} />
            </Flex>
          )}
        </Modal>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "1rem" }}>
            <AddIcon
              className="close-icon"
              onClick={() => onClose()}
              width={"1.5rem"}
              height={"1.5rem"}
            />
            <div style={{ display: "flex", justifyContent: "space-around", width: "50%" }}>
              {getFooter()}
            </div>
          </div>
          <Card
            className="card-style"
            style={{ padding: "1rem", margin: "1rem" }}
            styles={{ body: { padding: "0" } }}
            title={"Upload Job"}>
            {loading && (
              <Flex
                justify="space-around"
                style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
                <Spin tip="Loading, please wait..." />
              </Flex>
            )}
            {error && <ErrorViewer error={error} />}
            <Upload.Dragger {...uploadProps}>
              <Space direction="vertical" align="center">
                <CloudUploadOutlined className="custom-upload-icon" />
                <Typography.Text className="font-1212" style={{ fontSize: "1rem" }}>
                  Choose a file or drag and drop here
                </Typography.Text>
                <Typography.Text className="font-7777" style={{ fontSize: ".75rem" }}>
                  Maximum file size : 1 GB
                </Typography.Text>
                <Button type="primary">
                  Browse File
                </Button>
              </Space>
            </Upload.Dragger>{" "}
            {fileList.length > 0 && (
              <Flex gap={8} vertical>
                <Row gutter={16}>
                  <Col span={12}>
                    <Flex vertical align="center">
                      <Typography.Text>Location</Typography.Text>
                      <Select
                        key="location"
                        placeholder="Select"
                        variant="borderless"
                        className="drop-down-menu"
                        onChange={val => setSelectedLocationId(val)}
                        style={{ width: "100%" }}
                        options={locations}
                        fieldNames={{ label: "locationName", value: "locationId" }}
                        value={selectedLocationId}
                      />
                    </Flex>
                  </Col>
                  <Col span={12}>
                    <Flex vertical align="center">
                      <Typography.Text>Provider</Typography.Text>
                      <Select
                        placeholder="Select"
                        variant="borderless"
                        className="drop-down-menu"
                        onChange={val => setSelectedProviderId(val)}
                        key={account?.accountId}
                        style={{ width: "100%" }}
                        fieldNames={{ label: "providerName", value: "providerId" }}
                        options={providers}
                        value={selectedProviderId}
                      />
                    </Flex>
                  </Col>
                </Row>

                <UploadStats fileList={fileList} />
                <Flex
                  wrap="wrap"
                  gap="middle"
                  justify="space-between"
                  style={{ marginTop: "1.75rem" }}>
                  {(() => {
                    if (fileList.length === 0) {
                      return;
                    }

                    return fileList.map(item => <FileCard item={item} />);
                  })()}
                </Flex>
                {/* <Card className="card-style" styles={{ body: { padding: "0" } }}>
                  <Table
                    className="table-style"
                    columns={columns}
                    style={{ border: "none" }}
                    dataSource={fileList}
                    pagination={false}
                    rowKey="uid"
                    size="small"
                    components={{
                      body: {
                        cell: (props: any) => (
                          <td
                            {...props}
                            style={{
                              borderBottomWidth: "1px",
                              borderBottomColor: "#f0f0f0",
                              borderBottomStyle: "solid"
                            }}
                          />
                        )
                      }
                    }}
                  />
                </Card> */}
              </Flex>
            )}
          </Card>
        </>
      )}
    </>
  );
}
